import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { DetailsHistorieCardComponent } from '../../../components/details-historie-card/details-historie-card.component';
import { DetailsHistoriePanelDirective } from '../../../components/details-historie-card/details-historie-panel.directive';
import { PersonOption } from '../../../components/person-select/person-select.component';
import { SaveToastService } from '../../../components/save-toast/save-toast.service';
import { UebergabePanelComponent } from '../../../components/uebergabe-panel/uebergabe-panel.component';
import {
  aktiveStufe,
  aktivSeit,
  toUebergabeStufen,
  uebergabeFehlerKey,
  uebergebenGesperrt,
} from '../../../components/uebergabe-panel/uebergabe-kette';
import { VerlaufListeComponent } from '../../../components/verlauf-liste/verlauf-liste.component';
import { VersionDiffListComponent } from '../../../components/version-diff-list/version-diff-list.component';
import {
  DetailsHistorieStatus,
  DetailsHistorieTab,
  UebergabeEntwurf,
  UebergabeHandOff,
  VerlaufEintrag,
  VersionFeld,
} from '../../../models/details-historie.model';
import { HandoverChain } from '../../../models/handover-chain.model';
import { TestCase, TestCaseActivity, TestCaseRevision } from '../../../models/test-case.model';
import { HeaderInboxBadgeService } from '../../../services/header-inbox-badge.service';
import { TestCaseHandoverService } from '../../../services/test-case-handover.service';
import { TestCaseService } from '../../../services/test-case.service';
import { tcValueLabel } from '../../../pipes/tc-value-label.pipe';

const VERLAUF_ANZAHL = 20;
const TYP_KEY = 'testCases.form.activity.types.';
const VORLAGEN = ['testschritte', 'reviewen', 'testdaten', 'staging'] as const;

type VergleichsFeld = 'title' | 'description' | 'testGoal' | 'preconditions' | 'assigneeUserId' | 'testType';

const VERGLEICH: { feld: VergleichsFeld; label: string }[] = [
  { feld: 'title', label: 'testCases.form.title' },
  { feld: 'description', label: 'testCases.form.description' },
  { feld: 'testGoal', label: 'testCases.form.testGoal' },
  { feld: 'preconditions', label: 'testCases.form.preconditions' },
  { feld: 'assigneeUserId', label: 'testCases.form.properties.assignee' },
  { feld: 'testType', label: 'testCases.form.properties.testType' },
];

const OHNE_WERT_IN_ALTREVISIONEN: ReadonlySet<VergleichsFeld> = new Set(['testType']);

function alsText(wert: unknown): string {
  return wert === null || wert === undefined ? '' : String(wert);
}

@Component({
  selector: 'app-test-case-details-historie',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    DetailsHistorieCardComponent,
    DetailsHistoriePanelDirective,
    UebergabePanelComponent,
    VerlaufListeComponent,
    VersionDiffListComponent,
  ],
  templateUrl: './test-case-details-historie.component.html',
  styleUrl: './test-case-details-historie.component.scss',
})
export class TestCaseDetailsHistorieComponent {
  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);
  readonly current = input<TestCase | null>(null);
  readonly readonly = input<boolean>(false);
  readonly personOptions = input<PersonOption[]>([]);
  readonly currentUserId = input<string | null>(null);
  readonly notesFilled = input<boolean>(false);
  readonly chainChanged = output<void>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly testCaseService = inject(TestCaseService);
  private readonly handoverService = inject(TestCaseHandoverService);
  private readonly toast = inject(SaveToastService);
  private readonly inboxBadge = inject(HeaderInboxBadgeService);
  // Abhängigkeit der übersetzten Texte, damit sie beim Sprachwechsel neu entstehen.
  private readonly sprache = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly aktiverReiter = signal('');

  protected readonly kette = signal<HandoverChain | null>(null);
  protected readonly ketteStatus = signal<DetailsHistorieStatus>('bereit');
  protected readonly beschaeftigt = signal(false);
  protected readonly aktionsFehler = signal<string | null>(null);
  // Hält den Übergabe-Entwurf, weil der Reiterwechsel das Panel zerstört.
  readonly entwurf = signal<UebergabeEntwurf | null>(null);
  protected readonly vorversion = signal<TestCaseRevision | null>(null);
  protected readonly versionStatus = signal<DetailsHistorieStatus>('bereit');
  protected readonly verlaufStatus = signal<DetailsHistorieStatus>('bereit');
  protected readonly verlaufGekuerzt = signal(false);
  private readonly aktivitaeten = signal<TestCaseActivity[]>([]);
  private readonly verlaufAnzahl = signal<number | null>(null);
  private ketteAbruf: Subscription | null = null;
  private versionAbruf: Subscription | null = null;
  private verlaufAbruf: Subscription | null = null;

  protected readonly istBearbeiten = computed(() => !!this.testCaseId());
  protected readonly stufen = computed(() => toUebergabeStufen(this.kette()));
  protected readonly seit = computed(() => aktivSeit(this.kette()));
  private readonly inhaberId = computed(() => aktiveStufe(this.kette())?.userId ?? null);

  protected readonly auswahl = computed(() => {
    const ausgeschlossen = new Set([this.inhaberId(), this.currentUserId()]);
    return this.personOptions().filter((person) => !ausgeschlossen.has(person.id));
  });

  protected readonly sperrGrund = computed<string | null>(() => {
    this.sprache();
    return this.istBearbeiten() ? null : this.translate.instant('testCases.form.historie.erstNachAnlegen');
  });

  protected readonly uebergebenSperrGrund = computed<string | null>(() => {
    this.sprache();
    return uebergebenGesperrt(this.kette(), this.inhaberId())
      ? this.translate.instant('common.detailsHistorie.uebergabe.fehler.bereitsWeitergegeben', this.objekt())
      : null;
  });

  protected readonly vorlagen = computed<string[]>(() => {
    this.sprache();
    return VORLAGEN.map((key) => this.translate.instant(`testCases.form.historie.vorlagen.${key}`));
  });

  protected readonly geaenderteFelder = computed<VersionFeld[]>(() => {
    this.sprache();
    const aktuell = this.current();
    const vorher = this.vorversion();
    if (!aktuell || !vorher) return [];
    return VERGLEICH.filter(
      ({ feld }) =>
        !(OHNE_WERT_IN_ALTREVISIONEN.has(feld) && vorher[feld] == null) &&
        alsText(aktuell[feld]) !== alsText(vorher[feld]),
    ).map(({ feld, label }) => ({
      label: this.translate.instant(label),
      vorher: this.anzeigeWert(feld, vorher[feld]),
      nachher: this.anzeigeWert(feld, aktuell[feld]),
    }));
  });

  protected readonly verlaufEintraege = computed<VerlaufEintrag[]>(() => {
    this.sprache();
    return this.aktivitaeten().map((aktivitaet) => ({
      personName: aktivitaet.createdByName ?? '',
      ereignis: this.ereignisText(aktivitaet),
      detail: aktivitaet.type === 'HANDED_OVER' ? this.payloadText(aktivitaet, 'message') : null,
      zeit: aktivitaet.createdAt,
    }));
  });

  protected readonly reiter = computed<DetailsHistorieTab[]>(() => {
    this.sprache();
    const label = (id: string): string => this.translate.instant(`testCases.form.historie.reiter.${id}`);
    const tabs: DetailsHistorieTab[] = [];
    if (this.istBearbeiten()) {
      tabs.push({ id: 'versionen', label: label('versionen'), count: this.geaenderteFelder().length || null });
    }
    tabs.push(
      { id: 'uebergabe', label: label('uebergabe'), count: this.kette()?.stages.length || null },
      { id: 'verlauf', label: label('verlauf'), count: this.verlaufAnzahl() || null },
      { id: 'notizen', label: label('notizen'), count: this.notesFilled() ? 1 : null },
    );
    return tabs;
  });

  constructor() {
    effect(() => {
      const id = this.testCaseId();
      untracked(() => {
        this.kette.set(null);
        this.entwurf.set(null);
        this.aktionsFehler.set(null);
        this.vorversion.set(null);
        this.aktivitaeten.set([]);
        this.verlaufAnzahl.set(null);
        if (!id) return;
        this.ladeKette();
        this.ladeVorversion();
        this.ladeVerlauf();
      });
    });
  }

  ladeKette(): void {
    const id = this.testCaseId();
    if (!id) return;
    this.ketteAbruf?.unsubscribe();
    this.ketteStatus.set('laedt');
    this.ketteAbruf = this.handoverService
      .list(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (kette) => {
          this.kette.set(kette);
          this.ketteStatus.set('bereit');
        },
        error: () => this.ketteStatus.set('fehler'),
      });
  }

  ladeVorversion(): void {
    const id = this.testCaseId();
    if (!id) return;
    this.versionAbruf?.unsubscribe();
    this.versionStatus.set('laedt');
    this.versionAbruf = this.testCaseService
      .getPreviousRevision(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (revision) => {
          this.vorversion.set(revision);
          this.versionStatus.set('bereit');
        },
        error: () => this.versionStatus.set('fehler'),
      });
  }

  ladeVerlauf(): void {
    const id = this.testCaseId();
    if (!id) return;
    this.verlaufAbruf?.unsubscribe();
    this.verlaufStatus.set('laedt');
    this.verlaufAbruf = this.testCaseService
      .listActivities(this.projectId(), id, VERLAUF_ANZAHL)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (seite) => {
          this.aktivitaeten.set(seite.content);
          this.verlaufAnzahl.set(seite.totalElements);
          this.verlaufGekuerzt.set(!seite.last);
          this.verlaufStatus.set('bereit');
        },
        error: () => this.verlaufStatus.set('fehler'),
      });
  }

  protected uebergeben(handOff: UebergabeHandOff): void {
    const name = this.personOptions().find((person) => person.id === handOff.userId)?.displayName ?? '';
    this.fuehreAus(
      (projectId, id) =>
        this.handoverService.handOff(projectId, id, {
          userId: handOff.userId,
          message: handOff.message ?? '',
          notify: handOff.notify,
        }),
      true,
      () => {
        this.toast.show(this.translate.instant('testCases.form.historie.uebergebenAn', { name }));
        this.inboxBadge.refresh();
      },
    );
  }

  protected entwurfGeaendert(entwurf: UebergabeEntwurf | null): void {
    this.entwurf.set(entwurf);
    if (!entwurf) this.aktionsFehler.set(null);
  }

  protected abschliessen(): void {
    this.fuehreAus((projectId, id) => this.handoverService.close(projectId, id), true);
  }

  protected wiederOeffnen(): void {
    this.fuehreAus((projectId, id) => this.handoverService.reopen(projectId, id), true);
  }

  protected ueberspringen(stageId: string): void {
    this.fuehreAus((projectId, id) => this.handoverService.skip(projectId, id, stageId), false);
  }

  private fuehreAus(
    aufruf: (projectId: string, testCaseId: string) => Observable<HandoverChain>,
    meldeAenderung: boolean,
    danach?: () => void,
  ): void {
    const id = this.testCaseId();
    if (!id || this.readonly() || this.beschaeftigt()) return;
    this.beschaeftigt.set(true);
    aufruf(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (kette) => {
          this.kette.set(kette);
          this.beschaeftigt.set(false);
          this.aktionsFehler.set(null);
          danach?.();
          this.ladeVerlauf();
          if (meldeAenderung) this.chainChanged.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.beschaeftigt.set(false);
          this.aktionsFehler.set(this.translate.instant(uebergabeFehlerKey(err), this.objekt()));
          // Sonst hielte das Panel eine später geladene fremde Stufe für das eigene Ergebnis und leerte den Entwurf.
          this.entwurf.update((entwurf) => entwurf && { ...entwurf, stufenBeimAbsenden: null });
        },
      });
  }

  private objekt(): { objekt: string } {
    return { objekt: this.translate.instant('testCases.form.historie.objekt') };
  }

  private anzeigeWert(feld: VergleichsFeld, wert: unknown): string | null {
    const text = alsText(wert);
    if (!text) return null;
    if (feld === 'assigneeUserId') {
      return this.personOptions().find((person) => person.id === text)?.displayName ?? text;
    }
    if (feld === 'testType') return tcValueLabel(this.translate, 'testType', text);
    return text;
  }

  private ereignisText(aktivitaet: TestCaseActivity): string {
    switch (aktivitaet.type) {
      case 'HANDOVER_SKIPPED': {
        const skippedId = this.payloadText(aktivitaet, 'skippedUserId');
        const name = this.personOptions().find((person) => person.id === skippedId)?.displayName;
        return name
          ? this.translate.instant(`${TYP_KEY}HANDOVER_SKIPPED`, { name })
          : this.translate.instant(`${TYP_KEY}HANDOVER_SKIPPED_NO_NAME`);
      }
      case 'LINKED':
      case 'UNLINKED': {
        const reference = this.payloadText(aktivitaet, 'label') ?? this.payloadText(aktivitaet, 'reference');
        return reference
          ? this.translate.instant(`${TYP_KEY}${aktivitaet.type}`, { reference })
          : this.translate.instant(`${TYP_KEY}${aktivitaet.type}_NO_REF`);
      }
      case 'STEP_ADDED': {
        const action = this.payloadText(aktivitaet, 'action');
        return action
          ? this.translate.instant(`${TYP_KEY}STEP_ADDED_WITH_ACTION`, { action })
          : this.translate.instant(`${TYP_KEY}STEP_ADDED`);
      }
      default:
        return this.translate.instant(`${TYP_KEY}${aktivitaet.type}`);
    }
  }

  private payloadText(aktivitaet: TestCaseActivity, schluessel: string): string | null {
    const wert = aktivitaet.payload?.[schluessel];
    return typeof wert === 'string' && wert.trim() ? wert : null;
  }
}
